import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cns from 'classnames';
import Tab from './Tab';
import { deprecatedUsage } from '../Utils/devUtils';

/**
I tabs sono normalmente posizionati al di sopra del contenuto, e le loro label danno sempre indicazione di cosa si trova al loro interno.

Inserirli sempre una riga orizzontale.

Evidenziare il tab  che mostra il contenuto.

Creare una gerarchia nei diversi tabs.

Il contenuto del tab deve essere posizionato al di sotto e vicino per creare una relazione fra i due.
 */
class Tabs extends Component {
  constructor(props) {
    super(props);

    let storedIndex = null;
    if (props.store) {
      const datiStore = this.caricaStorage();
      if (datiStore) {
        storedIndex = datiStore.currentIndex;
      }
    }

    this.state = {
      index: storedIndex || props.initialIndex || props.index || 0,
      indexFromStore: !!storedIndex,
    };

    if (window.MutationObserver) {
      this.observer = new window.MutationObserver(this.aggiorna);
    }
  }

  componentDidMount() {
    // se l'index è stato impostato dallo store allora richiamo la callback onSelect
    const { indexFromStore, index } = this.state;
    const { onSelect } = this.props;
    if (indexFromStore && onSelect && index) {
      onSelect(index);
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { index } = this.props;

    deprecatedUsage(this, !nextProps.spaced, 'framework-ui-components.Tabs.spaced');

    if (nextProps.index !== index) {
      this.setState({ index: nextProps.index });
    }
  }

  componentWillUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  caricaStorage = () => {
    if (window.sessionStorage) {
      const { store } = this.props;
      const dati = window.sessionStorage.getItem(store);
      if (!dati) {
        return null;
      }
      return JSON.parse(dati);
    }
    return null;
  };

  salvaStorage = (dati) => {
    if (window.sessionStorage) {
      const { store } = this.props;
      window.sessionStorage.setItem(store, JSON.stringify(dati));
    }
  };

  aggiorna = () => {
    const { children } = this.props;
    const { index } = this.state;

    const tabs = React.Children.toArray(children).filter((child) => child).length;
    if ((index + 1) > tabs) {
      //  Se elimino tab elezionato scalo la selezione al precedente
      this.seleziona(index - 1);
    }
  };

  seleziona = (indice) => {
    const { index, onSelect, children } = this.props;

    if (index == null) {
      //  Se tab da selezionare è disabilitato scalo al tab precedente
      if (React.Children.toArray(children)[indice].props.disabled) {
        this.seleziona(indice - 1);
      } else {
        this.setState({ index: indice });
      }
    }
    const { store } = this.props;
    if (store) {
      this.salvaStorage({
        currentIndex: indice,
      });
    }
    onSelect && onSelect(indice);
  };

  static Tab = Tab;

  render() {
    const {
      action,
      className,
      children,
      index,
      vertical, spaced,
    } = this.props;
    const {
      index: sIndex,
    } = this.state;

    if (this.refTabs != null && !this.listener && window.MutationObserver) {
      this.listener = true;
      this.observer.observe(this.refTabs, {
        attributes: true,
        childList: true,
        subtree: true,
      });
    }

    const classes = cns(
      'tabs',
      {
        'tabs--vertical': vertical,
        'tabs--spaced': spaced,
      },
      className,
    );

    const actionClasses = cns(
      'tabs__action',
      {
        'tabs__action--vertical': vertical,
      },
    );

    const indexesClasses = cns(
      'tabs__indexes',
      {
        'tabs__index--underlined': !vertical,
      },
    );

    return (
      <div className={classes}>
        <div
          className={indexesClasses}
          ref={(r) => { this.refTabs = r; }}
        >
          {React.Children.map(React.Children.toArray(children), (tab, i) => {
            const tabClasses = cns(
              'tabs__index',
              {
                'tabs__index--selected': i === (index || sIndex),
                'tabs__index--disabled': tab.props.disabled,
              },
            );

            return (
              <div
                className={tabClasses}
                key={i} // eslint-disable-line react/no-array-index-key
                onClick={() => !tab.props.disabled && this.seleziona(i)}
                role="presentation"
              >
                <div className="tab__title">
                  {tab.props.title}
                  {tab.props.badge && <div className="tab__badge">{tab.props.badge}</div>}
                </div>
                {tab.props.action && <div className="tab__action">{tab.props.action}</div>}
              </div>
            );
          })}
          {action && (
            <div className={actionClasses}>
              {action}
            </div>
          )}
        </div>
        {React.Children.toArray(children)[(index || sIndex)]}
      </div>
    );
  }
}

Tabs.propTypes = {
  /** Action da renderizzare alla fine dei tabs */
  action: PropTypes.node,
  /** Contenuto da renderizzare all'interno del componente */
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  /** Classi css personalizzate da aggiungere al componente */
  className: PropTypes.string,
  /** Indica l'indice dell'elemento da selezionare,
     * sia all'inizializzazione del componente, sia all'update di questa proprietà */
  index: PropTypes.number,
  /** Indica l'indice dell'elemento da selezionare all'inizializzazione del componente */
  initialIndex: PropTypes.number,
  /**
   * Funzione di callback che verrà chiamata all'update del tab selezionato:
   * ```
   *  onSelect(index)
   * ```
   * dove:
   * * *index*: indice del tab selezionato
   */
  onSelect: PropTypes.func,
  /** Indica se lo scorrimento dei tab passa in verticale */
  vertical: PropTypes.bool,
  /**
  * Nome dell'oggetto salvato nello storage del browser
  */
  store: PropTypes.string,
  /**
Indica se l'area centrale deve essere più distanziata dalle tabs.

_NOTA: La proprietà è stata introdotta nella versione 1.8.23_
_con default `false` per mantenere compatibilità._
_Nella versione 1.9.0 verrà eliminata ed il componente si comporterà come `spaced=true`._
  */
  spaced: PropTypes.bool,
};

Tabs.defaultProps = {
  action: null,
  children: null,
  className: '',
  index: null,
  initialIndex: 0,
  onSelect: null,
  vertical: false,
  store: null,
  spaced: false,
};

export default Tabs;
